import React, { ComponentProps, useMemo, useRef, useState } from 'react';
import { isArray, last, uniq } from 'lodash';
import { ParentSize } from '@visx/responsive';
import { AxisBottom } from '@visx/axis';
import classNames from 'classnames';
import { toZonedTime } from 'date-fns-tz';
import { scaleTime } from '@visx/scale';

import { Grouping, TimeAggregationPeriod } from '@/explore/types';
import { NoMatchBanner } from '@/components/banner';
import { DropdownMenuItem } from '@/components/dropdown';
import { GroupFilter } from '@/explore/utils/drilldown';

import { buildDomainWithPadding, getTickFormatFn, getTickValues, GroupAxisHeight } from './utils';
import {
  flattenGroupedChartData,
  getChartCount,
  getLeftRightAxisTypes,
  getSeries,
} from '../grouped-chart/utils';
import {
  GroupedChartData,
  GroupedTimeSeriesData,
  isGroupedTimeSeriesData,
  TimeSeriesData,
} from '../grouped-chart/types';
import { GroupedChart } from '../grouped-chart';
import { NumYTicks, TimeSeriesChart } from './timeseries-chart';
import { StackedTimeSeriesChart } from './stacked-timeseries-chart';
import { getLegendData } from '../legend/utils';
import { Legend } from '../legend';
import { alignScale, getValueScale } from '../grouped-chart/utils/scale';

import { StackedGroupTimeSeriesChart } from './stacked-group-timeseries-chart';

import commonStyles, { multiChartChartHeight, minChartHeight } from '../charts.module.scss';

const MinChartHeight = parseInt(minChartHeight);
const MinChartWithSubAxisHeight = 400;
const WidthPerGroup = parseInt(commonStyles.widthPerGroup);
const YValuePaddingCoef = 0.2;
const HorizontalPadding = 20;
const VerticalPadding = 10;

export interface ChartColor {
  fill: string;
  highlight: string;
}

const getDates = (data: GroupedChartData<TimeSeriesData | GroupedTimeSeriesData>): Date[] =>
  isArray(data)
    ? data.flatMap((item) => getDates(item.items))
    : data.chartData.items.map((item) => item.dateValue);

const DefaultScalePadding = 0;
const BarChartScalePadding = 0.6;

interface GroupedTimeSeriesChartProps {
  data: GroupedChartData<TimeSeriesData | GroupedTimeSeriesData>;
  grouping: Grouping[];
  aggPeriod: TimeAggregationPeriod;
  stacked?: boolean;
  showTotals: boolean;
  onTimeClick?: (params: {
    x: number;
    y: number;
    key: string;
    groupFilters: GroupFilter[];
  }) => void;
  hideGrid?: boolean;
  timezone: string;
  isResized?: boolean;
  isTableVisible?: boolean;
  buildSeriesMenu?: (seriesKey: string) => DropdownMenuItem[];
}

export const GroupedTimeSeriesChart = (props: GroupedTimeSeriesChartProps) => {
  const { data, grouping, isResized = false, isTableVisible = true } = props;

  const dates = uniq(getDates(data));
  if (dates.length === 0) {
    return <NoMatchBanner />;
  }

  const customHeightEnabled = !isTableVisible && isResized;
  const chartCount = getChartCount(data);

  const isGroupedTimeSeries = flattenGroupedChartData(data).some((chartData) =>
    isGroupedTimeSeriesData(chartData),
  );
  const seriesDepth = isGroupedTimeSeries ? 3 : 2;
  const chartHeight = isGroupedTimeSeries
    ? MinChartWithSubAxisHeight
    : parseInt(multiChartChartHeight);
  const numChartGroups = Math.max(0, grouping.length - seriesDepth);
  const isMultiChart = numChartGroups > 0;

  return (
    <ParentSize
      debounceTime={0}
      className={classNames(commonStyles.chartContainer, {
        [commonStyles.customHeight]: customHeightEnabled,
      })}
      style={{
        minHeight:
          isMultiChart && !customHeightEnabled ? `${chartHeight * chartCount}px` : undefined,
      }}>
      {(parent) => <GroupedTimeSeriesChartInner {...props} parent={parent} />}
    </ParentSize>
  );
};
const GroupedTimeSeriesChartInner = (
  props: GroupedTimeSeriesChartProps & { parent: { width: number; height: number } },
) => {
  const {
    data,
    grouping,
    aggPeriod,
    stacked = false,
    showTotals,
    onTimeClick,
    hideGrid = false,
    timezone,
    buildSeriesMenu,
    parent,
  } = props;

  const [highlightedDate, setHighlightedDate] = useState<Date | null>(null);
  const footerRef = useRef<HTMLDivElement>(null);

  const series = getSeries(data);
  const dates = useMemo(() => uniq(getDates(data)), [data]);

  const groupKey = last(grouping)?.key;
  const containsBarChartSeries = series.some(({ chartType }) => chartType === 'bar');
  const chartCount = getChartCount(data);

  const isGroupedTimeSeries = flattenGroupedChartData(data).some((chartData) =>
    isGroupedTimeSeriesData(chartData),
  );
  const seriesDepth = isGroupedTimeSeries ? 3 : 2;
  const numChartGroups = Math.max(0, grouping.length - seriesDepth);
  const isMultiChart = numChartGroups > 0;
  const scalePadding = containsBarChartSeries ? BarChartScalePadding : DefaultScalePadding;

  const dateScale = useMemo(
    () =>
      scaleTime({
        domain: buildDomainWithPadding(dates[0], dates[dates.length - 1], aggPeriod, scalePadding),
        range: [0, parent.width - WidthPerGroup * numChartGroups],
      }),
    [dates, numChartGroups, parent.width, aggPeriod, scalePadding],
  );

  const zonedDateScale = dateScale
    .copy()
    .domain(dateScale.domain().map((date) => toZonedTime(date, timezone)));

  const footerHeight =
    // eslint-disable-next-line react-compiler/react-compiler
    (footerRef?.current?.clientHeight ?? 55) +
    (hideGrid ? 10 : 0) +
    (isGroupedTimeSeries ? GroupAxisHeight : 0);

  const chartHeight = Math.max(
    MinChartHeight,
    Math.floor(
      (parent.height -
        (footerHeight + (isMultiChart && isGroupedTimeSeries ? GroupAxisHeight : 0))) /
        chartCount,
    ),
  );

  const axisTypes = getLeftRightAxisTypes(series);

  const leftValueScale = useMemo(
    () =>
      getValueScale(
        data,
        stacked,
        'left',
        YValuePaddingCoef,
        [chartHeight, 0],
        NumYTicks,
        axisTypes.left === 'Integer' ? 1 : undefined,
      ),
    [axisTypes.left, chartHeight, data, stacked],
  );
  const rightValueScale = useMemo(
    () =>
      alignScale(
        getValueScale(
          data,
          stacked,
          'right',
          YValuePaddingCoef,
          [chartHeight, 0],
          NumYTicks,
          axisTypes.right === 'Integer' ? 1 : undefined,
        ),
        leftValueScale,
        NumYTicks,
        axisTypes.right === 'Integer' ? 1 : undefined,
      ),
    [axisTypes.right, chartHeight, data, leftValueScale, stacked],
  );
  const valueScales = useMemo(
    () => ({
      left: leftValueScale,
      right: rightValueScale,
    }),
    [leftValueScale, rightValueScale],
  );

  if (dates.length === 0) {
    return <NoMatchBanner />;
  }

  return (
    <div>
      <GroupedChart
        width={parent.width}
        widthPerGroup={WidthPerGroup}
        data={data}
        renderChart={(chartData, chartWidth, groupFilters) => {
          if (stacked) {
            if (isGroupedTimeSeriesData(chartData)) {
              const chartProps: ComponentProps<typeof StackedGroupTimeSeriesChart> = {
                data: chartData,
                dateScale,
                zonedDateScale,
                valueScales,
                aggPeriod,
                highlightedDate,
                width: chartWidth,
                height: chartHeight,
                hideGrid,
                groupKey,
                showTotals,
                onTimeClick: (input) =>
                  onTimeClick?.({
                    ...input,
                    groupFilters: [...groupFilters, ...input.groupFilters],
                  }),
                onDateHighlight: setHighlightedDate,
                timezone,
              };
              return <StackedGroupTimeSeriesChart {...chartProps} />;
            }
            const chartProps: ComponentProps<typeof StackedTimeSeriesChart> = {
              data: chartData,
              dateScale,
              zonedDateScale,
              valueScales,
              aggPeriod,
              highlightedDate,
              width: chartWidth,
              height: chartHeight,
              hideGrid,
              groupKey,
              showTotals,
              onTimeClick: (input) =>
                onTimeClick?.({
                  ...input,
                  groupFilters: [...groupFilters, ...input.groupFilters],
                }),
              onDateHighlight: setHighlightedDate,
              timezone,
            };
            return <StackedTimeSeriesChart {...chartProps} data={chartData} />;
          }

          if (isGroupedTimeSeriesData(chartData)) {
            throw new Error('Grouped time series chart is not supported');
          }

          const chartProps: ComponentProps<typeof TimeSeriesChart> = {
            data: chartData,
            dateScale,
            zonedDateScale,
            valueScales,
            aggPeriod,
            highlightedDate,
            width: chartWidth,
            height: chartHeight,
            hideGrid,
            groupKey,
            onTimeClick: (input) =>
              onTimeClick?.({
                ...input,
                groupFilters: [...groupFilters, ...input.groupFilters],
              }),
            onDateHighlight: setHighlightedDate,
            timezone,
          };
          return <TimeSeriesChart {...chartProps} data={chartProps.data} />;
        }}
      />
      <div ref={footerRef}>
        {!hideGrid && (
          <svg width={parent.width} height={25}>
            <AxisBottom
              left={Math.max(0, WidthPerGroup * numChartGroups)}
              top={0}
              scale={zonedDateScale}
              hideZero
              hideAxisLine
              tickClassName={commonStyles.tickLabel}
              tickLabelProps={() => ({
                textAnchor: scalePadding > 0 ? 'middle' : 'start',
                dy: -4,
              })}
              tickValues={getTickValues(aggPeriod ?? 'month', zonedDateScale)}
              tickFormat={getTickFormatFn(aggPeriod ?? 'month')}
              rangePadding={300}
            />
          </svg>
        )}

        <div
          style={{
            marginLeft: Math.max(HorizontalPadding, WidthPerGroup * numChartGroups),
            marginRight: HorizontalPadding,
            marginTop: hideGrid ? VerticalPadding : 0,
          }}>
          <div className={commonStyles.chartFooter}>
            <Legend data={getLegendData(data)} buildSeriesMenu={buildSeriesMenu} />
          </div>
        </div>
      </div>
    </div>
  );
};
